import { FolderPath, folderPathAncestry, folderPathName } from '@lib/library/folderPath';
import ChevronRightRounded from '@mui/icons-material/ChevronRightRounded';
import { useLibrary } from '@stores/Library/Library';
import { useLibraryFolder } from '@stores/Library/LibraryFolder';
import { observer } from 'mobx-react';
import { FolderGlyph } from './FolderGlyph';
import { FolderCrumb } from './LibraryFolderBar';
import css from './LibraryTitleTrail.module.scss';

/**
 * The library header's title while a folder is open: the library, the folders above, and the
 * open folder — so the page is titled by where you are and the stats line under it describes that
 * folder. An `h2` because it takes the library name's place, including its role as the one title
 * in the row that gives up width (`LibraryContentHeaderLeft.module.scss`); the ancestors shrink
 * before the open folder's name does.
 */
export const LibraryTitleTrail = observer(function LibraryTitleTrail({
  path,
}: {
  path: FolderPath;
}) {
  const folder = useLibraryFolder();
  const library = useLibrary();
  const editable = !library.isReadOnly;

  return (
    <h2 className={css.trail} title={`${library.name ?? ''}${path}`}>
      {folderPathAncestry(path)
        .slice(0, -1)
        .map((ancestor) => (
          <span key={ancestor} className={css.step}>
            <FolderCrumb
              path={ancestor}
              editable={editable}
              className={css.crumb}
              showIcon={false}
            />
            <ChevronRightRounded className={css.separator} />
          </span>
        ))}
      <span className={css.current} aria-current="page">
        <FolderGlyph color={folder.foldersByPath.get(path)?.color} open size={22} />
        <span className={css.currentName}>{folderPathName(path)}</span>
      </span>
    </h2>
  );
});
